import { type ReactNode } from 'react';

import { Box } from '@mui/material';

import TabBar, { type TabBarItem } from '@/designs-dev/device/TabBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

/**
 * A tab of the app, with the tab bar under it.
 *
 * MainTabs supplies the bar when the app is running, so no screen carries its
 * own — a screen that did would draw two. A flows page mounts one screen on its
 * own and gets neither, which is how a tab ends up drawn without the thing that
 * makes it a tab.
 *
 * So this is the composition, in one place: the same three lines were written
 * out in InAppHome and again wherever a spec wanted a tab.
 */
const AppTab = ({
  active,
  items,
  tone,
  onSelect,
  action,
  children,
}: {
  /** Which tab is lit. */
  active: string;
  /** The tabs, where a concept changes them. */
  items?: TabBarItem[];
  /** The bar's colouring. See TabBar's `tone`. */
  tone?: 'lime' | 'neutral';
  /** A tab tapped. See TabBar's `onSelect`. */
  onSelect?: (id: string) => void;
  /** A button in the tab bar. See TabBar's `action`. */
  action?: ReactNode;
  children: ReactNode;
}) => {
  return (
    <ScreenRoot relative={tone === 'neutral'}>
      <Box
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {children}
      </Box>
      <TabBar
        active={active}
        items={items}
        tone={tone}
        onSelect={onSelect}
        action={action}
      />
    </ScreenRoot>
  );
};

export default AppTab;
